
import Vue from 'vue';
import _trim from 'lodash/trim';
import { db } from '@/firebase';
import { mapState, mapActions, mapGetters } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { Company, CompanyCustomFieldDef } from '@run-diffusion/shared';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import SetTeamAllocationSettings from '@/views/Team/TeamDashboard/SetTeamAllocationSettings/SetTeamAllocationSettings.vue';
import EditCompanyCustomFieldDefs from './EditCompanyCustomFieldDefs.vue';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';

export default Vue.extend({
	name: 'EditCompany',
	props: {
		value: { type: Boolean, default: false },
	},
	data () {
		const SHORT_TEXT_LENGTH: number = 50;
		return {
			// FORM
			formValid: true,
			SHORT_TEXT_LENGTH,
			DEFAULT_TEXT_MAX_LENGTH,
			rules: {
				name: [
					getTextMaxLengthRule(DEFAULT_TEXT_MAX_LENGTH),
					v => !!_trim(v) || 'Required',
				],
				displayLabelTeam: [
					getTextMaxLengthRule(SHORT_TEXT_LENGTH),
				],
			},
			companyName: null,
            displayLabelTeam: null,
			teamCustomFieldDefs: [] as CompanyCustomFieldDef[],
			runnitCustomFieldDefs: [] as CompanyCustomFieldDef[],

			monthlyRunnitTokensBudget: 0,
			isBoundToBudget: false,
			leaveTeamUnfundedOnCreation: false,
			saving: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		sumMonthlyRunnitTokensBudget(): number {
			return this.team?.company?.sumMonthlyRunnitTokensBudget || 0;
		},
		computedDisplayLabelTeam () {
			return this.displayLabelTeam || 'Team';
		},
	},
	watch: {
		value: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && newVal !== oldVal) {
					this.initLocalValues(this.team?.company);
				}
			},
		},
		'team.company': {
			immediate: true,
			handler (newVal: Company, oldVal: Company) {
				const newCompanyId: string = newVal?.id || null;
				const oldCompanyId: string = oldVal?.id || null;
				if (newCompanyId !== oldCompanyId) {
					this.initLocalValues(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		getShowTextCounterAt,
		onEnterpriseBudgetsUpsell () {
			this.setUpsellDialog({ noCompanyBudgets: true });
		},
		setOpen (val: boolean) {
			this.$emit('input', val);
		},
		onClose () {
			this.setOpen(false);
		},
		async onSave () {
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			const trimmedCompanyName: string = _trim(this.companyName);
			const trimmedDisplayLabelTeam: string = _trim(this.displayLabelTeam);
			this.saving = true;
			try {
				// Update the company name directly in Firestore
				const updateData: any = {
					name: trimmedCompanyName,
					displayLabels: {
						team: trimmedDisplayLabelTeam || null,
					},
					teamCustomFieldDefs: this.teamCustomFieldDefs,
					runnitCustomFieldDefs: this.runnitCustomFieldDefs,
					'allotmentSettings.defaults': {
						monthlyRunnitTokensBudget: this.monthlyRunnitTokensBudget,
						isBoundToBudget: this.isBoundToBudget,
						leaveTeamUnfundedOnCreation: this.leaveTeamUnfundedOnCreation,
					},
				};

				await db.collection('companies').doc(this.team?.company?.id).update(updateData);

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Company saved: ${trimmedCompanyName}`,
					show: true,
				});
				this.onClose();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving company, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		onTeamAllocationSettingsInput ({ monthlyRunnitTokensBudget, isBoundToBudget, leaveTeamUnfundedOnCreation }) {
			this.monthlyRunnitTokensBudget = monthlyRunnitTokensBudget || 0;
			this.isBoundToBudget = !!isBoundToBudget;
			this.leaveTeamUnfundedOnCreation = !!leaveTeamUnfundedOnCreation;
		},
		initLocalValues (company: Company) {
			this.companyName = company?.name || null;
			this.displayLabelTeam = company?.displayLabels?.team || null;
			this.teamCustomFieldDefs = company?.teamCustomFieldDefs || [];
			this.runnitCustomFieldDefs = company?.runnitCustomFieldDefs || [];
			this.monthlyRunnitTokensBudget = company?.allotmentSettings?.defaults?.monthlyRunnitTokensBudget || 0;
			this.isBoundToBudget = !!company?.allotmentSettings?.defaults?.isBoundToBudget;
			this.leaveTeamUnfundedOnCreation = !!company?.allotmentSettings?.defaults?.leaveTeamUnfundedOnCreation;
		},
	},
	components: {
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		SetTeamAllocationSettings,
		EditCompanyCustomFieldDefs,
	},
});
